import { FileListResponse, FileMetadata } from '../../api/types/cms';
import useApi from '../../api';
import {
    MutableRefObject,
    ReactElement,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    Avatar,
    Box,
    Chip,
    CircularProgress,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    MenuItem,
    Stack,
    Typography,
} from '@mui/material';
import { useImageBuilder } from '../../utils/image';
import { useDateFormatter } from '../../utils/date';
import { useSnackbar } from 'notistack';
import { useTranslation } from '../../contexts/TranslationContext';

import ContextMenu from '../qlive/ContextMenu';
import { getReadableFileSizeString } from '../../utils/static-files';
import config from '../../config';

import './FileVersions.component.css';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    LoadingOutlined,
} from '@ant-design/icons';
import AutomaticPublishingFileChip from './AutomaticPublishingFileChip';
import { getActiveFileVersion } from '../../utils/cms';

const DEFAULT_LIST_ITEM_WIDTH = 150;

export type FileVersionsRef = {
    loadData: (force: boolean) => void;
};

type FileVersionsProps = {
    file?: FileMetadata | null;
    onPreview?: (file: FileMetadata, openInEditMode: boolean) => void;
    onChangeDates?: (file: FileMetadata) => void;
    onClose?: (removed: boolean) => void;
    onSelectionUpdate?: (selectedFile: FileMetadata | null) => void;
    classRef?: MutableRefObject<FileVersionsRef | undefined>;
};

export default function FileVersions({
    file,
    onPreview = () => {},
    onChangeDates = () => {},
    onClose = (removed: boolean = false) => {},
    onSelectionUpdate = () => {},
    classRef,
}: FileVersionsProps) {
    const lastLoaded = useRef<string>();
    const { enqueueSnackbar } = useSnackbar();

    const { cms } = useApi();
    const { translate } = useTranslation();
    const { formatDate } = useDateFormatter();
    const { buildImageUrl } = useImageBuilder();

    const [loading, setLoading] = useState(false);
    const [versions, setVersions] = useState<FileListResponse>();

    useEffect(() => {
        if (versions) {
            onSelectionUpdate(getActiveFileVersion(versions));
        }
    }, [versions, onSelectionUpdate]);

    const loadData = (force: boolean = false) => {
        if (
            file &&
            (Boolean(file.file_id && lastLoaded.current !== file.file_id) ||
                force) &&
            !loading
        ) {
            lastLoaded.current = file.file_id;
            setLoading(true);
            cms.getVersionsByFile(file.file_id)
                .then(response => {
                    if (response && !response.error) {
                        setVersions(response);
                    } else {
                        onClose(true);
                        setVersions([]);
                    }
                    setLoading(false);
                })
                .catch(() => {
                    onClose(true);
                    setLoading(false);
                });
        }
    };

    useEffect(() => {
        if (classRef) {
            classRef.current = {
                loadData,
            };
        }
    }, [classRef]);

    useEffect(loadData, [file]);

    const removeVersion = (fileToDelete: FileMetadata) => {
        cms.deleteFileByFileId(fileToDelete.file_id, fileToDelete.id).then(
            deleteResponse => {
                if (
                    deleteResponse &&
                    !deleteResponse.error &&
                    deleteResponse.success
                ) {
                    enqueueSnackbar(translate('messages.deleted'), {
                        variant: 'success',
                    });
                    loadData(true);
                }
            },
        );
    };

    const toggleVersion = (fileToToggle: FileMetadata, enabled: boolean) => {
        cms.toggleFile(fileToToggle.id, { enabled }).then(toggleResponse => {
            if (toggleResponse && !toggleResponse.error) {
                if (toggleResponse.enabled === enabled) {
                    enqueueSnackbar(translate('messages.saved'), {
                        variant: 'success',
                    });
                    loadData(true);
                    onSelectionUpdate(toggleResponse);
                }
                if (toggleResponse.uses_automatic_publishing) {
                    enqueueSnackbar(
                        translate('messages.cms.files.cant_toggle_automatic'),
                        {
                            variant: 'warning',
                        },
                    );
                }
            }
        });
    };

    const renderVersion = (version: FileMetadata, index: number) => {
        const hasPreview = version.mime_type?.match(/^image\/.*$/) ?? false;

        const downloadFile = () => {
            window.open(
                new URL(
                    `/asset/file/${version.file_id}?version=${version.version}&download=true`,
                    config.apiHost,
                ),
                '_blank',
            );
        };

        const getContextMenuItems = (
            handleClose: () => void,
        ): Array<ReactElement> => {
            const onDelete = () => {
                removeVersion(version);
                handleClose();
            };

            const onToggle = () => {
                toggleVersion(version, !version.enabled);
                handleClose();
            };

            const onDateChange = () => {
                onChangeDates(version);
                handleClose();
            };

            const items = [
                <MenuItem key="download" onClick={downloadFile}>
                    {translate('form.general.download')}
                </MenuItem>,
                <MenuItem key="toggle" onClick={onToggle}>
                    {translate('form.general.toggle')}
                </MenuItem>,
                <MenuItem key="set-dates" onClick={onDateChange}>
                    {translate('pages.cms.files.dates')}
                </MenuItem>,
                <MenuItem key="delete" onClick={onDelete}>
                    {translate('form.general.delete')}
                </MenuItem>,
            ];

            if (hasPreview) {
                const onPreviewThis = () => {
                    onPreview(version, false);
                    handleClose();
                };

                const onEditThis = () => {
                    onPreview(version, true);
                    handleClose();
                };

                items.unshift(
                    <MenuItem key="edit" onClick={onEditThis}>
                        {translate('form.general.edit')}
                    </MenuItem>,
                );
                items.unshift(
                    <MenuItem key="preview" onClick={onPreviewThis}>
                        {translate('form.general.preview')}
                    </MenuItem>,
                );
            }

            return items;
        };

        const dimensions = {
            width: DEFAULT_LIST_ITEM_WIDTH,
            height: 0,
        };

        const ratio = version.additional_data?.ratio?.split(':');
        if (ratio && ratio[0] && ratio[1]) {
            dimensions.height =
                (DEFAULT_LIST_ITEM_WIDTH / parseInt(ratio[0])) *
                parseInt(ratio[1]);
        }

        return (
            <ListItem
                key={`${version.file_id}-${version.version}`}
                className="fileVersion"
                sx={{ mx: -0.5 }}
                divider={index + 1 < (versions?.length ?? 0)}>
                <ContextMenu menuItems={getContextMenuItems}>
                    <Stack
                        spacing={2}
                        direction="row"
                        alignItems="center"
                        sx={{ py: 2 }}>
                        {hasPreview && (
                            <ListItemAvatar className="imageContainer">
                                <Avatar
                                    src={buildImageUrl(version, 300)}
                                    onClick={event => {
                                        event.stopPropagation();
                                        onPreview(version, false);
                                    }}
                                    sx={dimensions}
                                    className="fileVersionImage"
                                />
                            </ListItemAvatar>
                        )}
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={2}
                            flex={1}>
                            <Stack spacing={1}>
                                <Stack spacing={0}>
                                    <Typography variant="caption" component="p">
                                        {`${translate('pages.cms.files.version')}: ${version.version}`}
                                    </Typography>
                                    <Typography
                                        variant="subtitle1"
                                        component="p">
                                        {version.original_name}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.primary"
                                        component="p">
                                        {formatDate(version.created_at)}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        component="p">
                                        {getReadableFileSizeString(
                                            version.size ?? 0,
                                        )}
                                    </Typography>
                                </Stack>
                                <div>
                                    {version.uses_automatic_publishing && (
                                        <AutomaticPublishingFileChip
                                            version={version}
                                        />
                                    )}
                                </div>
                            </Stack>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={2}>
                                <Stack
                                    alignItems="center"
                                    direction="column"
                                    spacing={1}>
                                    <Typography
                                        variant="overline"
                                        fontWeight="bold"
                                        component="p">
                                        {version.content_name ??
                                            translate(
                                                'pages.cms.files.default_content',
                                            )}
                                    </Typography>
                                    <Chip label={version.mime_type} />
                                </Stack>
                                <IconButton
                                    title={translate('form.general.toggle')}
                                    color={
                                        version.enabled
                                            ? version.uses_automatic_publishing
                                                ? 'info'
                                                : 'success'
                                            : 'error'
                                    }
                                    onClick={() =>
                                        toggleVersion(version, !version.enabled)
                                    }>
                                    {version.enabled ? (
                                        <CheckCircleOutlined
                                            style={{ fontSize: 20 }}
                                        />
                                    ) : (
                                        <CloseCircleOutlined
                                            style={{ fontSize: 20 }}
                                        />
                                    )}
                                </IconButton>
                            </Stack>
                        </Stack>
                    </Stack>
                </ContextMenu>
            </ListItem>
        );
    };

    return useMemo(() => {
        if (loading) {
            return (
                <Box
                    display="flex"
                    minHeight={200}
                    width="100%"
                    alignItems="center"
                    justifyContent="center">
                    <CircularProgress />
                </Box>
            );
        }

        return (
            <Stack>
                <Typography variant="subtitle1" sx={{ px: 2, pt: 2 }}>
                    {translate('pages.cms.files.all_versions')}
                </Typography>
                {loading ? (
                    <Stack
                        minHeight={300}
                        alignItems="center"
                        justifyContent="center">
                        <LoadingOutlined />
                    </Stack>
                ) : (
                    <List sx={{ m: 0, p: 0 }}>
                        {versions?.map(renderVersion)}
                    </List>
                )}
            </Stack>
        );
    }, [loading, translate]);
}
